import { gql, useQuery } from "@apollo/client";
import { useEffect, useState } from "react";
import "../BalanceSheet/Reports.scss";
import HeaderDiv from "../../Components/HeaderDiv/HeaderDiv";
import { useDispatch, useSelector } from "react-redux";
import { Loading } from "../../Components";
import ReportsMenu from "../BalanceSheet/ReportsMenu";
import { NumberFormatter } from "../../CommonFunction/NumberFormatter/NumberFormatter";
import { useGetPrintReport } from "../../custom_hooks/useGetPrintReport";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { FaFileExcel } from "react-icons/fa";

const AllReportsData = gql`
  query getAccountDataForProfitAndLossReport($startDate: ISODate, $endDate: ISODate) {
    getAccountDataForProfitAndLossReport(startDate: $startDate, endDate: $endDate)
  }
`;
const ProfitAndLoss = () => {
  const [formdata, setformdata] = useState({});
  const [SumObj, setSumObj] = useState(0);
  let Refetch = useSelector((store) => store.FormFieldData.refresh);
  let Date = useSelector((store) => store?.financialYear?.SelectedDate);
  const ToDate = Date.ToDate;
  const FromDate = Date.ToDate;
  const dispatch = useDispatch();
  const { fetchReport, report, error } = useGetPrintReport();
  const {
    data: Reports,
    refetch,
    loading,
  } = useQuery(AllReportsData, {
    variables: {
      startDate: FromDate,
      endDate: ToDate,
    },
    fetchPolicy: "network-only",
  });

  function flattenWithTotals(groups) {
    const rows = [];

    function walk(grp, depth = 0) {
      // 1️⃣ Group header
      rows.push({ name: grp.AccountGroup.Account_Group, amount: null, depth, isHeader: true });

      // 2️⃣ Each account
      grp.AccountGroup.accounts.forEach((acct) => {
        const a = acct.account || acct;
        rows.push({
          name: a.Account_Name || a.Name,
          amount: a.DisplaytotalClosingBalanceTillThat,
          depth: depth + 1,
        });
      });

      // 3️⃣ Nested sub-groups
      (grp.GroupUnderData || []).forEach((sub) => walk(sub, depth + 1));

      // 4️⃣ Subtotal for this group
      rows.push({
        // name: `Total ${grp.AccountGroup.Account_Group}`,
        name: ` `,
        amount: grp.DisplayGroupClosing,
        depth,
        isTotal: true,
      });
    }

    (groups || []).forEach((g) => walk(g, 0));
    return rows;
  }
  const exportToExcel = async () => {
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Balance Sheet");

    /* 1️⃣  Columns */
    ws.columns = [
      { header: "Expenses", key: "aName", width: 40 },
      { header: "Amount", key: "aAmt", width: 20 },
      { header: "", key: "spacer", width: 4 },
      { header: "Income", key: "lName", width: 40 },
      { header: "Amount", key: "lAmt", width: 20 },
    ];

    /* 2️⃣  Push header down two rows */
    ws.spliceRows(1, 0, [], []);

    /* 3️⃣  (logo block – unchanged) */

    /* 4️⃣  Company name (left-aligned) */
    ws.mergeCells("A2:E2");
    const titleCell = ws.getCell("A2");
    titleCell.value = report?.Company_Name || "";
    titleCell.font = { size: 16, bold: true };
    titleCell.alignment = { horizontal: "left", vertical: "middle" };

    /* 5️⃣  Colours & borders */
    const grpFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD9D9D9" } }; // grey
    const subFill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE8F4FF" } }; // blue
    const thin = { style: "thin" };
    const border = { top: thin };

    /* 🔸 Helper now lives here (top-level inside the function) */
    const paint = (cell, opts = {}) => {
      if (opts.fill) cell.fill = opts.fill;
      if (opts.font) cell.font = opts.font;
      if (opts.border) cell.border = opts.border;
      if (opts.alignment) cell.alignment = opts.alignment;
    };

    /* 6️⃣  Flatten data */
    const ExpenseData = flattenWithTotals(formdata?.Expense?.accountGroupsData);
    const IncomeData = flattenWithTotals(formdata?.Income?.accountGroupsData);
    const maxLen = Math.max(ExpenseData.length, IncomeData.length);

    for (let i = 0; i < maxLen; i++) {
      const A = ExpenseData[i] || {};
      const L = IncomeData[i] || {};

      const row = ws.addRow({
        aName: A.name ?? "",
        aAmt: A.amount ?? "",
        spacer: "",
        lName: L.name ?? "",
        lAmt: L.amount ?? "",
      });

      /* —— ASSETS —— */
      const aName = row.getCell(1),
        aAmt = row.getCell(2);
      aName.alignment = { indent: (A.depth || 0) * 2 };

      if ((A.isHeader || A.isTotal) && A.depth === 0) {
        [aName, aAmt].forEach((c) => paint(c, { fill: grpFill, font: { bold: true }, border }));
        if (A.isTotal) aAmt.alignment = { horizontal: "right" };
      }
      if ((A.isHeader || A.isTotal) && A.depth > 0) {
        [aName, aAmt].forEach((c) => paint(c, { fill: subFill, font: { bold: true }, border }));
        if (A.isTotal) aAmt.alignment = { horizontal: "right" };
      }

      /* —— LIABILITIES —— */
      const lName = row.getCell(4),
        lAmt = row.getCell(5);
      lName.alignment = { indent: (L.depth || 0) * 2 };

      if ((L.isHeader || L.isTotal) && L.depth === 0) {
        [lName, lAmt].forEach((c) => paint(c, { fill: grpFill, font: { bold: true }, border }));
        if (L.isTotal) lAmt.alignment = { horizontal: "right" };
      }
      if ((L.isHeader || L.isTotal) && L.depth > 0) {
        [lName, lAmt].forEach((c) => paint(c, { fill: subFill, font: { bold: true }, border }));
        if (L.isTotal) lAmt.alignment = { horizontal: "right" };
      }
    }

    /* 7️⃣  Currency columns */
    [2, 5].forEach((col) => {
      ws.getColumn(col).numFmt = "#,##0.00";
      ws.getColumn(col).alignment = { horizontal: "right" };
    });

    /* 8️⃣  Grand total */
    ws.addRow([]);
    const grand = ws.addRow({
      aName: "Total Expense",
      aAmt: formdata?.Expense?.DisplayTotalClosing || 0,
      spacer: "",
      lName: "Total Income",
      lAmt: formdata?.Income?.DisplayTotalClosing || 0,
    });
    [1, 2, 4, 5].forEach((c) =>
      paint(grand.getCell(c), {
        font: { bold: true },
        border,
        alignment: { horizontal: c === 2 || c === 5 ? "right" : "left" },
      })
    );

    /* 9️⃣  Save */
    const buf = await wb.xlsx.writeBuffer();
    saveAs(
      new Blob([buf], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "ProfitAndLossReport.xlsx"
    );
  };

  useEffect(() => {
    if (Refetch) {
      refetch({
        variables: {
          startDate: FromDate,
          endDate: ToDate,
        },
      });
      dispatch({ type: "Refetch", payload: false });
    }
  }, [Refetch]);
  useEffect(() => {
    if (Reports) {
      setformdata(Reports?.getAccountDataForProfitAndLossReport?.Result);

      setSumObj(Reports?.getAccountDataForProfitAndLossReport?.Result?.Difference);
    }
  }, [Reports]);
  useEffect(() => {
    refetch({
      variables: {
        startDate: FromDate,
        endDate: ToDate,
      },
    });
  }, [FromDate, ToDate, refetch]);

  return (
    <div className="reportsdatacontainer">
      <HeaderDiv
        title={"Profit And Loss Report"}
        Children={
          <div className="headerChild" style={{ display: "flex", gap: 16 }}>
            <span>Difference: {SumObj}</span>
            <button
              onClick={exportToExcel}
              disabled={loading}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: loading ? "not-allowed" : "pointer",
                opacity: loading ? 0.5 : 1,
              }}
            >
              <FaFileExcel size={24} />
            </button>
          </div>
        }
      />
      {loading ? (
        <Loading />
      ) : (
        <div className="reportsdata">
          <div className="Reporsdatasub">
            <h3>Expenses</h3>
            <hr />
            <ReportsMenu
              data={formdata?.Expense?.accountGroupsData}
              type="Expenses"
              Profit={{ Net_Profit: formdata?.Expense?.DisplayNet_Profit }}
            />
            <div className="TotalContainer">Expenses Total:{formdata?.Expense?.DisplayTotalClosing}</div>
          </div>
          <div className="Reporsdatasub">
            <h3>Income</h3>
            <hr />
            <ReportsMenu
              data={formdata?.Income?.accountGroupsData}
              type={"Income"}
              Profit={{ Net_Loss: formdata?.Income?.DisplayNet_Loss }}
            />
            <div className="TotalContainer">Income Total:{formdata?.Income?.DisplayTotalClosing}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfitAndLoss;
